import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Validators, UntypedFormBuilder, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PasswordValidators } from '../../validators/password.validator';
import { Store } from '@ngrx/store';
import * as fromApp from './../../store/app.reducer';
import * as AuthActions from './../store/auth.actions';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],    
    standalone: false,
})
export class ResetPasswordComponent implements OnInit {
    public formSubmitted: boolean = false;
    public showPasswords: boolean = false;
    public showReTypePasswords: boolean = false;

    public resetPasswordForm = this.formBuilder.group({
        password: ['', [Validators.required, Validators.minLength(8), PasswordValidators.complexityValidator]],
        confirmPassword: ['', [Validators.required]]
    }, {
        validators: this.passwordMatchValidator
    });

    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private location: Location,
        private translate: TranslateService,
        private store: Store<fromApp.AppState>
    ) { }

    goBack(): void {
        this.location.back();
    }

    ngOnInit(): void {
        this.setupFormValidation();
    }

    get password() {
        return this.resetPasswordForm.get('password');
    }

    get confirmPassword() {
        return this.resetPasswordForm.get('confirmPassword');
    }

    private setupFormValidation(): void {
        this.resetPasswordForm.valueChanges.subscribe(() => {
            if (this.formSubmitted) {
                this.validateForm();
            }
        });
    }

    private validateForm(): void {
        if (this.resetPasswordForm.valid) {
            this.formSubmitted = true;
            return;
        }
    }

    public shouldShowError(controlName: string): boolean {
        const control = this.resetPasswordForm.get(controlName);
        return Boolean((control?.touched || this.formSubmitted) && control?.errors);
    }

    public getErrorMessage(controlName: string): string {
        const control = this.resetPasswordForm.get(controlName);
        if (!control || !control.errors) return '';

        const errors = control.errors;
        const controlNameCapitalized = controlName.charAt(0).toUpperCase() + controlName.slice(1);

        if (errors['required']) {
            return `${controlNameCapitalized} ${this.translate.instant('form.validation.required')}`;
        }
        if (errors['email']) {
            return this.translate.instant('form.validation.email');
        }
        if (errors['minlength']) {
            return `${controlNameCapitalized} ${this.translate.instant('form.validation.min-length', { minLength: errors['minlength'].requiredLength })}`;
        }
        if (errors['passwordMismatch']) {
            return this.translate.instant('form.validation.passwords-not-match');
        }
        if (errors['passwordComplexity']) {
            const missing = errors['passwordComplexity'].missing;
            const requirements = [];

            if (missing.upperCase) requirements.push(this.translate.instant('form.validation.uppercase-letter'));
            if (missing.lowerCase) requirements.push(this.translate.instant('form.validation.lowercase-letter'));
            if (missing.numeric) requirements.push(this.translate.instant('form.validation.number'));
            if (missing.specialChar) requirements.push(this.translate.instant('form.validation.special-character'));

            return `${controlNameCapitalized} ${this.translate.instant('form.validation.must-include')}: ${requirements.join(', ')}`;
        }

        return '';
    }

    public togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
        if (field === 'password') {
            this.showPasswords = !this.showPasswords;
        } else {
            this.showReTypePasswords = !this.showReTypePasswords;
        }
    }

    public getPasswordFieldType(field: 'password' | 'confirmPassword'): string {
        return field === 'password' ?
            (this.showPasswords ? 'text' : 'password') :
            (this.showReTypePasswords ? 'text' : 'password');
    }

    // Form Submission Helper
    public onSubmit(): void {
        this.formSubmitted = true;
        if (this.resetPasswordForm.valid) {
            const formData = {
                token: this.route.snapshot.queryParams['token'],
                password: this.password.value,
                confirmPassword: this.confirmPassword.value
            };
            this.store.dispatch(new AuthActions.ResetPasswordStart(formData));
        } else {
            this.formSubmitted = false;
            Object.keys(this.resetPasswordForm.controls).forEach(key => {
                const control = this.resetPasswordForm.get(key);
                control?.markAsTouched();
            });
        }
    }

    // Password Validation Helper
    private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
        const password = control.get('password');
        const confirmPassword = control.get('confirmPassword');

        if (password && confirmPassword && password.value !== confirmPassword.value) {
            confirmPassword.setErrors({ passwordMismatch: true });
            return { passwordMismatch: true };
        }
          return null;
    }
}